import { s } from '@/theme/tokens';
import { Box, Text } from '@/ui/primitives';
import { landingCopy } from './landingCopy';
import { landingSpacing } from './landingSpacing';
import { LANDING_BODY_FONT_SIZE } from './landingTypography';
import type { ReactElement } from 'react';

type TestingChangedSectionProps = {
  width: number;
};

const renderSectionTitle = (title: string): ReactElement => (
  <Text
    role="sectionTitle"
    component="h2"
    transform="none"
    sx={{
      display: 'flex',
      alignItems: 'baseline',
      gap: s('sm'),
      pb:
        title === landingCopy.testingChanged.sectionTitles.conclusion
          ? landingSpacing.testingChanged.conclusionTitleMarginBottom
          : landingSpacing.testingChanged.sectionTitleMarginBottom,
      mb: s('none'),
    }}
  >
    <Box
      component="img"
      src="/logo.png"
      alt=""
      aria-hidden
      sx={(theme) => ({
        width: theme.app.iconSizes.lg,
        height: theme.app.iconSizes.lg,
        display: 'block',
        flexShrink: 0,
        transform: 'translateY(3px)',
      })}
    />
    <Box component="span">{title}</Box>
  </Text>
);

const renderBullet = (bullet: string): ReactElement => {
  if (bullet.includes('(and when)')) {
    const [before, after] = bullet.split('(and when)');
    return (
      <Text role="bodyMuted" component="span" sx={{ fontSize: LANDING_BODY_FONT_SIZE }}>
        {before}(
        <Box component="span" sx={{ fontStyle: 'italic' }}>
          and when
        </Box>
        ){after}
      </Text>
    );
  }

  if (bullet.includes("(it's always that one test)")) {
    const [before, after] = bullet.split("(it's always that one test)");
    return (
      <Text role="bodyMuted" component="span" sx={{ fontSize: LANDING_BODY_FONT_SIZE }}>
        {before}(
        <Box component="span" sx={{ fontStyle: 'italic' }}>
          it&apos;s always that one test
        </Box>
        ){after}
      </Text>
    );
  }

  return (
    <Text role="bodyMuted" component="span" sx={{ fontSize: LANDING_BODY_FONT_SIZE }}>
      {bullet}
    </Text>
  );
};

export const TestingChangedSection = ({ width }: TestingChangedSectionProps): ReactElement => (
  <Box component="section">
    <Box sx={{ maxWidth: width, mx: 'auto' }}>
      <Box sx={{ mb: landingSpacing.testingChanged.awarenessToStrugglesGap }}>
        {renderSectionTitle(landingCopy.testingChanged.sectionTitles.awareness)}
        <Box sx={{ maxWidth: `calc(${s('5xl')} + ${s('5xl')} + ${s('5xl')} + ${s('5xl')})` }}>
          {landingCopy.testingChanged.awarenessParagraphs.map((paragraph, index) => (
            <Text
              key={paragraph}
              role="bodyMuted"
              component="p"
              sx={{ fontSize: LANDING_BODY_FONT_SIZE }}
              mb={
                index === landingCopy.testingChanged.awarenessParagraphs.length - 1 ? 'none' : 'md'
              }
            >
              {paragraph}
            </Text>
          ))}
        </Box>
      </Box>

      <Box sx={{ mb: landingSpacing.testingChanged.strugglesToConclusionGap }}>
        {renderSectionTitle(landingCopy.testingChanged.sectionTitles.struggles)}
        <Box
          component="ul"
          sx={{
            m: s('none'),
            pl: s('xl'),
            color: 'text.secondary',
            mb: s('none'),
            display: 'grid',
            rowGap: s('sm'),
          }}
        >
          {landingCopy.testingChanged.bullets.map((bullet) => (
            <li key={bullet}>{renderBullet(bullet)}</li>
          ))}
        </Box>
      </Box>

      {renderSectionTitle(landingCopy.testingChanged.sectionTitles.conclusion)}
    </Box>
  </Box>
);
